import type { Messages } from './es';

export const en: Messages = {
  app: { name: 'HabitOS', tagline: 'Small steps, every day.' },
  nav: { today: 'Today', habits: 'Habits', brain: 'Brain', chat: 'Chat', stats: 'Progress', settings: 'Settings', main: 'Main navigation' },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    add: 'Add',
    create: 'Create',
    confirm: 'Confirm',
    undo: 'Undo',
    restore: 'Restore',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    optional: 'optional',
    loading: 'Loading…',
    more: 'More actions',
    dash: '—',
    today: 'Today',
    yesterday: 'Yesterday',
    retry: 'Retry',
    learnMore: 'Learn more',
  },
  onboarding: {
    title: 'Welcome to HabitOS',
    p1: 'Track habits you want to build, habits you want to cut down and one-off tasks.',
    p2: 'Works offline and without an account. Your data stays on this device.',
    p3: 'An account is optional: you can create one later to sync, get reminders and use WhatsApp.',
    accountMore: 'What an account adds',
    dayStart: 'Your day ends at',
    dayStartHelp: 'Anything you log before this time counts for the previous day. Handy if you go to bed late.',
    weekStart: 'Weeks start on',
    start: 'Get started',
  },
  today: {
    tryWith: 'Or try saying',
    greeting: { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' },
    askTitle: 'Talk to your assistant',
    askPlaceholder: 'Ask your assistant something…',
    suggestionsLabel: 'Chat suggestions',
    suggest: {
      read: 'Quiero leer 20 minutos cada noche',
      routine: 'Organízame la semana para estudiar, entrenar y descansar',
      water: 'Recuérdame beber agua todos los días a las 11',
      today: '¿Qué tengo hoy?',
      week: 'Organízame la semana',
      neglected: '¿Qué hábitos estoy descuidando?',
    },
    consistency: 'Consistency over the last 7 days: {pct} % ({done} of {total})',
    consistencyNone: 'No closed days yet to measure your consistency.',
    planWithChat: 'Plan with the chat',
    seeAgenda: 'See agenda',
    upNext: 'Now and next',
    now: 'Now',
    nothingTimed: 'Nothing else with a time today.',
    overdueTasks: 'You have {count} overdue task.|You have {count} overdue tasks.',
    title: 'Today',
    summary: '{done} of {total} done',
    allDone: 'All done for today',
    emptyTitle: 'Let’s start your day',
    emptyBody: 'Tell the assistant what you want to achieve and it will propose a plan, or create your first habit. Something small counts too.',
    emptyCta: 'Create a habit',
    nothingTodayTitle: 'Nothing scheduled today',
    nothingTodayBody: 'None of your habits are due today. Enjoy the break.',
    hiddenCount: '{count} hidden by filters',
    tasks: 'Tasks',
    ungrouped: 'No group',
    showFilters: 'Filters',
    hideCompleted: 'Hide completed',
    hideSkipped: 'Hide skipped',
    hideFailed: 'Hide missed',
    installTitle: 'Install HabitOS',
    installBody: 'Open it like an app, use it offline and get reminders.',
    installAction: 'Install',
    installIosBody: 'In Safari, tap Share and then "Add to Home Screen".',
    dismiss: 'Not now',
    tzTitle: 'You changed time zone',
    tzBody: 'Your device is in {device}. HabitOS uses {current}. Past entries will not move.',
    tzUseNew: 'Use {device}',
    tzKeep: 'Keep {current}',
    backupReminder: 'Your data only lives on this device. Export a copy or create an account so you do not lose it.',
    backupAction: 'Export now',
  },
  habit: {
    kinds: { build: 'Build', quit: 'Cut down or quit', task: 'Task' },
    kindHelp: {
      build: 'Something you want to do regularly.',
      quit: 'Something you want to limit. You set a maximum allowed.',
      task: 'Something you do once.',
    },
    status: {
      pending: 'Pending',
      completed: 'Done',
      missed: 'Missed',
      failed: 'Marked as failed',
      skipped: 'Skipped',
      vacation: 'Vacation',
      not_scheduled: 'Not scheduled',
      out_of_range: 'Outside the habit period',
      overdue: 'Overdue',
      onTrack: 'Within limit',
    },
    actions: {
      complete: 'Mark as done',
      uncomplete: 'Unmark',
      increment: 'Add {amount}',
      decrement: 'Subtract {amount}',
      record: 'Log',
      recordOccurrence: 'Log one',
      skip: 'Skip',
      unskip: 'Remove skip',
      move: 'Move to another day',
      undoMove: 'Move back',
      fail: 'Mark as failed',
      unfail: 'Remove failed mark',
      note: 'Add note',
      details: 'View details',
      edit: 'Edit habit',
      duplicate: 'Duplicate',
      archive: 'Archive',
      unarchive: 'Reactivate',
      delete: 'Delete',
      timer: 'Timer',
    },
    skipHelp: 'Skipping is for days with a valid reason (illness, travel). It does not break your streak or count in your rate.',
    moveHelp: 'Move just this one to another day. The repeat rule does not change, and your streak holds if you complete the new day.',
    moveSoonest: 'Next free days',
    movedAway: 'Moved to {date}.',
    movedHere: 'Moved here from {date}.',
    moveDateLabel: 'New day',
    movedToast: 'Moved to {date}.',
    moveUndoneToast: 'Back on its original day.',
    failHelp: 'Marking as failed records that it was not achieved, even if there are entries.',
    skipConfirmTitle: 'Skip this period',
    reasonLabel: 'Reason',
    progress: '{value} of {target}',
    progressUnit: '{value} of {target} {unit}',
    averageNeed: 'Today {value} of {need} {unit} · average {avg} of {target}',
    averageMet: 'Today {value} {unit} · average {avg} of {target}',
    limitUsed: '{value} of a maximum of {target}',
    times: '{value} of {target} times',
    remaining: '{amount} left',
    duplicateSuffix: '(copy)',
    deletedToast: 'Habit deleted',
    archivedToast: 'Habit archived',
    loggedToast: 'Logged',
    skippedToast: 'Skipped',
    unskipToast: 'Skip removed',
    autoUnskip: 'You logged activity on a skipped day, so the skip was removed.',
    streakDays: '{count} day|{count} days',
    streakWeeks: '{count} week|{count} weeks',
    streakMonths: '{count} month|{count} months',
  },
  editor: {
    newTitle: 'New habit',
    editTitle: 'Edit habit',
    type: 'Type',
    name: 'Name',
    namePlaceholder: 'For example: Read for 20 minutes',
    nameRequired: 'Enter a name.',
    description: 'Description',
    goal: 'Goal',
    goalBinary: 'Mark as done',
    goalNumeric: 'Amount',
    target: 'Target',
    limit: 'Maximum allowed',
    limitHelp: 'You succeed if you do not exceed this number. 0 means never.',
    unit: 'Unit',
    unitCustom: 'Other…',
    unitOne: 'Unit (singular)',
    unitOther: 'Plural',
    unitOnePlaceholder: 'glass',
    unitOtherPlaceholder: 'glasses',
    unitCustomHelp: 'The singular is used with 1 ("1 glass") and the plural otherwise. Leave the plural empty if it does not change.',
    unitRequired: 'Enter the unit.',
    average: 'Meet it on average',
    averageHelp: 'A slow day is offset by the previous ones: it counts if the average over the last days reaches the goal. A day with nothing logged never counts.',
    averageWindow: 'Days in the average',
    increment: 'Quick increment',
    incrementHelp: 'How much each tap adds on the Today view.',
    frequency: 'Frequency',
    freq: {
      daily: 'Every day',
      weekdays: 'Specific days',
      weekly_quota: 'Times per week',
      monthdays: 'Days of the month',
      monthly_quota: 'Times per month',
      interval_days: 'Every few days',
      yearly: 'Once a year',
    },
    everyWeeks: 'Repeat',
    everyWeeksOption: 'Every week|Every {count} weeks',
    yearlyHelp: 'Repeats every year on {date}, the start date.',
    quotaMode: 'How it counts',
    quotaTimes: 'Number of days achieved',
    quotaAmount: 'Total amount for the period',
    timesPerWeek: 'Times per week',
    timesPerMonth: 'Times per month',
    everyNDays: 'Every how many days',
    monthDays: 'Days of the month',
    lastDay: 'Last day of the month',
    startDate: 'Start date',
    endDate: 'End date',
    noEndDate: 'No end date',
    dueDate: 'Due date',
    dueTime: 'Time',
    duration: 'Duration (minutes)',
    durationHelp: 'How much time it takes in your agenda. Used to warn you about overlaps.',
    appearance: 'Appearance',
    icon: 'Icon',
    color: 'Color',
    group: 'Main group',
    secondaryGroups: 'Other groups',
    hideStreak: 'Hide this habit’s streak',
    excludedFromProgress: 'Leave out of the day’s progress',
    excludedFromProgressHelp: 'You still log it and it keeps its own stats and streak, but it does not count towards the day’s percentage or the completed-days map.',
    applyHistory: 'Also apply to history',
    applyHistoryHelp: 'By default, changes to the target or frequency apply from today and the past is kept.',
    applyHistoryImpact: '{count} past period will change status.|{count} past periods will change status.',
    errors: {
      invalid_target: 'The target is not valid.',
      end_before_start: 'The end date cannot be before the start date.',
      empty_schedule: 'Pick at least one day.',
      invalid_schedule: 'Check the frequency.',
      quit_schedule_not_allowed: 'This frequency is not available for cut-down habits.',
      generic: 'Check the highlighted fields.',
    },
    planLimit: 'You reached the free plan limit of {limit} active habits. Archive one or upgrade to Premium.',
  },
  units: {
    count: 'time|times',
    min: 'min',
    h: 'h',
    km: 'km',
    steps: 'step|steps',
    ml: 'ml',
    l: 'l',
    pages: 'page|pages',
    kcal: 'kcal',
    g: 'g',
  },
  weekdays: {
    short: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    long: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  },
  habits: {
    title: 'Habits',
    active: 'Active',
    archived: 'Archived',
    trash: 'Deleted',
    trashHelp: 'Deleted habits can be restored.',
    groups: 'Groups',
    newGroup: 'New group',
    groupName: 'Group name',
    deleteGroup: 'Delete group',
    deleteGroupBody: 'Habits in the group are not deleted.',
    moveUp: 'Move {name} up',
    moveDown: 'Move {name} down',
    empty: 'No habits in this list.',
    search: 'Search habits',
    noResults: 'No habit matches your search.',
  },
  detail: {
    currentStreak: 'Current streak',
    bestStreak: 'Best streak',
    successRate: 'Overall success',
    last30: 'Last 30 days',
    gaps30: 'Breaks in 30 days',
    gapsLongest: 'Longest: {length}',
    gapsHelp: 'How many times the streak was broken: several missed periods in a row count as one. Skipped days and vacations do not break it.',
    completions: 'Times done',
    relapse: 'Days within limit',
    avgProgress: 'Average progress',
    calendar: 'Calendar',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    history: 'Day history',
    selectDay: 'Pick a day on the calendar to view or fix its entries.',
    noEntries: 'No entries this day.',
    addEntry: 'Add entry',
    amount: 'Amount',
    entryNote: 'Note',
    deleteEntry: 'Delete entry',
    achievements: 'Achievements',
    noAchievements: 'No achievements yet. They come with consistency.',
    legend: 'Legend',
    reminders: 'Reminders',
    rateHelp: 'Share of periods achieved. Skipped days and vacations do not count.',
    retroNote: 'You can fix any day from the start date up to today.',
  },
  achievements: {
    earned: 'New achievement in {habit}: {label}!',
    earnedMany: 'New achievement in {habit}: {label}, and {count} more!',
    streak: '{count} streak',
    total: 'Done {count} time|Done {count} times',
  },
  stats: {
    goals: {
      title: 'Your goals',
      period: '{from} – {to}',
      atRisk: '{count} habit below {threshold} %: worth reviewing.|{count} habits below {threshold} %: worth reviewing.',
      allGood: 'No habit below the consistency threshold in these days.',
      few: 'Not enough data',
      risk: 'At risk',
      askChat: 'Ask the chat for a plan to get back on track',
    },
    title: 'Progress',
    overall: 'Last 30 days',
    completedPeriods: 'Periods achieved',
    activeHabits: 'Active habits',
    bestCurrent: 'Longest active streak',
    perHabit: 'By habit',
    empty: 'Once you log activity, your progress will show here.',
    premiumHint: 'Weekly trends are available with Premium.',
    weekly: 'Weekly completion (last 12 weeks)',
    heatTitle: 'Completed days',
    heatHelp: 'Each square is a day. The darker it is, the more of that day’s habits were completed.',
    weekday: {
      title: 'Weekday pattern',
      help: 'Share of days completed on each weekday, over the last 90 days.',
      best: 'Your best day is {day} ({rate}).',
      worst: 'The hardest one is {day} ({rate}).',
      flat: 'No clear differences between days yet.',
      few: 'More logged days are needed before a pattern shows.',
      cell: '{day}: {rate} ({done} of {total})',
    },
  },
  settings: {
    title: 'Settings',
    general: 'General',
    language: 'Language',
    theme: 'Theme',
    themes: { system: 'Automatic', light: 'Light', dark: 'Dark' },
    time: 'Day and week',
    dayStart: 'Day start time',
    dayStartHelp: 'New entries use this time. Existing entries do not change.',
    weekStart: 'First day of the week',
    weekStartHelp: 'The change applies when the current week ends.',
    timezone: 'Time zone',
    tzFollow: 'Follow device',
    tzFixed: 'Fixed',
    hideStreaks: 'Hide all streaks',
    vacations: 'Vacations',
    vacationsHelp: 'During a vacation, habits are skipped automatically and streaks are kept.',
    newVacation: 'New vacation',
    vacationName: 'Name',
    vacationScope: 'Applies to',
    scopeAll: 'All habits',
    scopeSelected: 'Selected habits',
    noVacations: 'No vacations yet.',
    data: 'Data',
    exportJson: 'Export full backup (JSON)',
    exportCsv: 'Export entries (CSV)',
    import: 'Import backup',
    importHelp: 'Import a file exported by HabitOS. Data is never duplicated.',
    importPreview: 'The file contains {habits} habits and {entries} entries.',
    importImpact: 'On import · New: {added} · Changes existing data: {updated} · Unchanged: {unchanged}',
    importOverwriteHelp: 'Changed data is replaced with the file version when it is more recent. If unsure, save a copy of what you have now first.',
    importBackupFirst: 'Export a current copy first',
    importOtherAccount: 'This file belongs to another account ({email}). If you import it, its data will be added to this device and, when signed in, to your account.',
    importAnalyzing: 'Analyzing the file…',
    importUnknown: 'The file has {count} item this version of HabitOS does not recognize; it will not be imported. Update the app to import it.|The file has {count} items this version of HabitOS does not recognize; they will not be imported. Update the app to import them.',
    importInvalid: '{count} invalid item will be skipped.|{count} invalid items will be skipped.',
    importDone: 'Import finished: {count} changes applied.',
    importFailed: 'The backup could not be imported. Your data has not changed. Please try again.',
    importErrors: {
      not_json: 'The file is not valid JSON.',
      wrong_format: 'The file is not a HabitOS export.',
      newer_version: 'The file comes from a newer version. Update the app.',
      invalid_structure: 'The file structure is not valid.',
    },
    storage: 'Storage',
    storagePersisted: 'The browser will keep your data persistently.',
    storageNotPersisted: 'The browser may delete data when space runs low. Install the app or export backups.',
    account: 'Account and sync',
    notifications: 'Notifications',
    plan: 'Plan',
    about: 'About',
    version: 'Version {version}',
    privacy: 'Privacy',
    privacyText: 'HabitOS uses no ads or tracking. Without an account, your data never leaves this device.',
    wipe: 'Erase all data on this device',
    wipeConfirm: 'All habits and entries on this device will be erased. This cannot be undone. Continue?',
  },
  security: {
    sessionsTitle: 'Open sessions',
    sessionsHelp: 'Devices and browsers signed in to your account. Sign out of any you do not recognize.',
    deviceOn: '{browser} on {os}',
    unknownDevice: 'Unknown device',
    thisDevice: 'This device',
    sessionDates: 'Last active: {last} · Signed in: {created}',
    revoke: 'Sign out',
    revokeNamed: 'Sign out {device}',
    revokeOthers: 'Sign out all other sessions',
    sessionRevoked: 'Session signed out.',
    othersRevoked: '{count} session signed out.|{count} sessions signed out.',
    passkeysTitle: 'Passkeys',
    passkeysHelp: 'Sign in without a code, using your device unlock. Remove the ones from devices you no longer use.',
    passkeysWhere: 'Each passkey is stored on your device or in its password manager (for example Apple or Google). HabitOS only keeps its public part, which cannot sign you in by itself. Removing it here makes it stop working; to remove it from the device too, delete it in its password manager.',
    noPasskeys: 'You have no passkeys.',
    passkeyName: 'Passkey {n}',
    passkeyCreated: 'Created: {created} · Not used yet',
    passkeyDates: 'Created: {created} · Last used: {used}',
    removePasskey: 'Remove passkey',
    removePasskeyNamed: 'Remove {name}',
    removePasskeyTitle: 'Remove this passkey?',
    removePasskeyBody: 'You will no longer be able to sign in with it. You can still sign in with an email code and create another passkey.',
    passkeyRemoved: 'Passkey removed.',
    loadFailed: 'The list could not be loaded. Check your connection.',
    retry: 'Retry',
    actionFailed: 'The action could not be completed. Check your connection and try again.',
  },
  assistant: {
    open: 'Assistant',
    title: 'Assistant',
    intro: 'Type what you want to do. You will see a proposal before anything is saved.',
    inputLabel: 'What would you like to do?',
    inputHelp: 'For example: “Remind me to study tomorrow at 6pm”.',
    interpret: 'Interpret',
    examplesTitle: 'Try',
    examples: {
      habit: 'Create a habit to read 20 minutes on weekdays',
      reminder: 'Remind me to study tomorrow at 6pm',
      list: 'Add eggs and milk to the shopping list',
      today: 'Show my tasks for today',
      log: 'Log 30 minutes of exercise',
    },
    unsupported: 'I did not understand that. Try creating a habit or task, a reminder, adding to a list, logging an activity, a note, the journal or a focus session.',
    clarifyTitle: 'I need a clarification',
    rephrase: 'Rephrase it to continue.',
    proposalTitle: 'Proposal',
    kinds: {
      create_habit: 'Create habit',
      create_task: 'Create task',
      add_to_list: 'Add to a list',
      show_today: 'See today’s tasks (agenda)',
      log_activity: 'Log activity',
      create_note: 'Save to brain',
      journal: 'Write in the journal',
      start_focus: 'Start a focus session',
      update_habit: 'Change habit',
      set_reminder: 'Habit reminder',
      reschedule_task: 'Reschedule task',
      log_focus: 'Log focus session',
      archive_habit: 'Archive habit',
      delete_habit: 'Delete habit',
    },
    fields: {
      category: 'Category',
      name: 'Name',
      type: 'Type',
      frequency: 'Frequency',
      goal: 'Goal',
      max: 'At most {value} {unit}',
      binaryGoal: 'Mark as done',
      date: 'Date',
      noDate: 'No date',
      time: 'Time',
      noTime: 'No time',
      reminder: 'Reminder',
      reminderYes: 'Yes, at that time',
      reminderNo: 'No',
      list: 'List',
      newList: '{name} (new)',
      items: 'Items',
      habit: 'Habit',
      amount: 'Amount',
      markDone: 'Mark as done',
      text: 'Text',
      duration: 'Duration',
    },
    freq: { daily: 'Every day', quota: '{count} time per week|{count} times per week', everyWeeks: '{days}, every {count} weeks', yearly: 'once a year' },
    reminderNote: 'The alert arrives as a notification if you have an account and notifications are enabled on this device.',
    warnDelete: '“{name}” will move to the trash. You can undo right after or restore it from Habits.',
    warnArchive: '“{name}” will no longer appear in Today. Its history is kept and you can restore it.',
    confirm: { default: 'Confirm', archive_habit: 'Archive', delete_habit: 'Delete' },
    editInForm: 'Edit in the form',
    undone: 'Undone.',
    q: {
      invalidTime: 'That time does not exist. Can you write it again (for example, 18:00)?',
      invalidDate: 'That date does not exist. Can you write it again?',
      whatTask: 'What should I remind you about?',
      pastDate: 'That date has passed. Choose a date from today on.',
      timePassed: 'Today at {time} has passed. Move it to tomorrow at the same time?',
      whatTime: 'What time do you want the reminder?',
      whatHabit: 'Which habit do you want to create?',
      futureLog: 'Activities cannot be logged on future days.',
      unitMismatch: 'I cannot convert that amount to the unit of “{name}”. Write it in its unit.',
      whichHabit: 'Which habit?',
      noHabit: 'You do not have a habit called “{name}”. Create it?',
      whatItems: 'What do you want to add to the list?',
      noHabitToChange: 'I cannot find a habit matching “{name}”.',
      focusRange: 'A focus session must last between 1 and 180 minutes.',
    },
    done: {
      habit: 'Habit “{name}” created.',
      task: 'Task “{name}” created.',
      reminderActive: 'Task “{name}” created with a reminder: you will get a notification at that time.',
      reminderSaved: 'Task “{name}” created and reminder saved. The notification will only be sent once you have an account and notifications enabled on this device.',
      list: '{count} item added to {list}.|{count} items added to {list}.',
      today: 'Here is what is pending today.',
      alreadyDone: '“{name}” was already marked today.',
      marked: '“{name}” marked as done.',
      logged: 'Logged: {amount} to “{name}”.',
      idea: 'Idea saved to your brain.',
      fact: 'Saved in your Brain · Fact.',
      quote: 'Saved in your Brain · Quote.',
      contact: 'Saved in your Brain · Contact.',
      note: 'Note saved to your brain.',
      journal: 'Saved to the journal.',
      focusBusy: 'A focus session is already running.',
      focus: '{count}-minute focus session started.|{count}-minute focus session started.',
      habitReminderActive: 'Habit «{name}» created with a reminder at {time}: you will get a notification.',
      habitReminderSaved: 'Habit «{name}» created and reminder at {time} saved. The notification will only be sent once you have an account and notifications enabled on this device.',
      updated: '«{name}» updated from today.',
      reminderSetActive: 'Reminder for «{name}» at {time}: you will get a notification.',
      reminderSetSaved: 'Reminder for «{name}» at {time} saved. The notification will only be sent once you have an account and notifications enabled on this device.',
      rescheduled: 'Task «{name}» rescheduled.',
      focusLogged: 'Focus session of {count} minute logged.|Focus session of {count} minutes logged.',
      many: 'Plan saved: {count} change.|Plan saved: {count} changes.',
      archived: '“{name}” archived.',
      deleted: '“{name}” deleted.',
    },
  },
  agenda: {
    dayModes: { hours: 'Hours' },
    timeline: 'Day timeline',
    nowAt: 'Now: {time}',
    view: 'View',
    list: 'List',
    agenda: 'Agenda',
    dayNav: 'Change day',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    today: 'Today',
    backToToday: 'Back to today',
    summary: '{pending} pending · {done} done',
    slots: { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', anytime: 'Any time' },
    emptyTitle: 'Nothing planned',
    emptyBodyToday: 'Today’s habits, tasks and important dates will show up here, sorted by time.',
    emptyBodyOther: 'There are no habits, tasks or important dates for this day.',
    moveTomorrow: 'Move to tomorrow',
    moveOther: 'Another date',
    moveTitle: 'Reschedule task',
    moveConfirm: 'Reschedule',
    moved: 'Task rescheduled.',
    scope: 'Period',
    scopes: { day: 'Day', week: 'Week' },
    conflictsSummary: 'There is {count} schedule clash.|There are {count} schedule clashes.',
    overlaps: 'Overlaps with {names}',
    estimated: 'estimated duration of 30 minutes',
    estimatedLegend: '* Estimated 30-minute duration: set it on the task or habit for more accurate clash detection.',
    dropHere: 'Drop the task here',
    weekNav: 'Change week',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    thisWeek: 'This week',
    weekRange: '{from} – {to}',
    backToThisWeek: 'Back to this week',
    exportIcs: 'Export (.ics)',
    openDay: 'Open day: {day}',
    free: 'Free',
  },
  focus: {
    title: 'Focus',
    open: 'Focus session',
    openFor: 'Focus on this habit',
    duration: 'Duration',
    minutes: '{count} minute|{count} minutes',
    custom: 'Other duration (minutes)',
    customHelp: 'Between {min} and {max} minutes.',
    durationError: 'Choose a duration between {min} and {max} minutes.',
    linkTo: 'Link to a habit or task',
    noLink: 'Not linked',
    label: 'Label',
    start: 'Start',
    session: 'Session in progress',
    remaining: '{time} left',
    running: 'Running',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    finish: 'Finish',
    cancel: 'Cancel',
    cancelTitle: 'Cancel the session?',
    cancelBody: 'The time from this session will not be saved.',
    cancelConfirm: 'Cancel the session',
    keepGoing: 'Keep going',
    doneTitle: 'Session complete',
    finishEarlyTitle: 'Finish early',
    doneBody: 'Focus time: {count} minute.|Focus time: {count} minutes.',
    logMinutes: 'Log {count} min to {name}',
    markDone: 'Mark {name} as done today',
    completeTask: 'Mark the task {name} as done',
    save: 'Save session',
    saved: 'Session saved: {count} minute.|Session saved: {count} minutes.',
    limitation: 'Time keeps counting even if you close or reload the page. The web cannot alert you when it ends if the app is closed.',
    statsTitle: 'Focus',
    statsSummary: 'Today: {today} min · This week: {week} min',
    statsSessions: '{count} session this week|{count} sessions this week',
    statsUnlinked: 'Not linked',
  },
  journal: {
    stats: {
      current: 'Current streak',
      best: 'Best streak',
      entries: 'Entries',
      week: 'Days written this week',
      written: 'written',
      notWritten: 'no entry',
      upcoming: 'upcoming',
      letters: { 1: 'M', 2: 'T', 3: 'W', 4: 'T', 5: 'F', 6: 'S', 7: 'S' },
      reviewIn: '{count} day until your next weekly review.|{count} days until your next weekly review.',
      reviewToday: 'The week closes today: a good moment for your review.',
    },
    dayNav: 'Change journal day',
    write: 'Write',
    editor: 'Journal entry',
    text: 'What would you like to note about today?',
    optional: 'Optional: answer only if you feel like it.',
    prompts: {
      went_well: 'What went well?',
      remember: 'What do you want to remember?',
      differently: 'What would you do differently?',
      grateful: 'What are you grateful for today?',
      tomorrow: 'What would you like to do tomorrow?',
    },
    anotherPrompt: 'Another question',
    includeInReview: 'Include in the weekly review',
    includeHelp: 'Only the entries you mark appear in the review.',
    inReview: 'In the review',
    saving: 'Saving…',
    saved: 'Saved',
    done: 'Done',
    deleted: 'Entry deleted.',
    emptyTitle: 'No entries this day',
    emptyBody: 'A few lines are enough. The questions are optional.',
    entryAt: 'Entry at {time}',
  },
  review: {
    open: 'Weekly review',
    title: 'Weekly review',
    weekNav: 'Change week',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    inProgress: 'Week in progress: counted up to today.',
    habits: 'Habits',
    consistency: '{done} of {total} periods completed',
    noPeriods: 'No closed periods yet this week.',
    previous: 'Previous week: {value}',
    habitLine: '{done} of {total}',
    howCounted: 'Completed, missed and failed periods count. Skipped days, vacations and the current day do not.',
    other: 'Tasks and focus',
    tasks: 'Tasks completed: {count}',
    focus: 'Focus: {minutes} min in {count} sessions',
    reflections: 'Reflections you chose to include',
    noReflections: 'No reflections included this week. You can mark them in the journal.',
  },
  brain: {
    title: 'Brain',
    tabs: { notes: 'Notes', lists: 'Lists', journal: 'Journal' },
    backToChat: 'Go to chat',
    kinds: { note: 'Note', idea: 'Idea', link: 'Link', date: 'Important date', list: 'List', quote: 'Quote', fact: 'Fact', contact: 'Contact' },
    author: 'Author',
    details: 'Details',
    contactHelp: 'Phone, email or anything you want to remember about this person.',
    quickAdd: 'Add to brain',
    kindLabel: 'Type',
    titleLabel: 'Title',
    quickHelp: 'You can paste a link directly.',
    url: 'Link (https://…)',
    date: 'Date',
    body: 'Text',
    tags: 'Tags',
    tagsHelp: 'Comma separated, for example: health, ideas',
    linkedHabits: 'Link to habits or tasks',
    pinned: 'Pin to top',
    add: 'Add',
    search: 'Search your brain',
    filters: 'Filters',
    all: 'All',
    saved: 'Saved.',
    invalid: 'Check the data: a title is required and links must start with http:// or https://.',
    deleted: 'Deleted.',
    edit: 'Edit',
    editNamed: 'Edit {title}',
    pinNamed: 'Pin {title}',
    unpinNamed: 'Unpin {title}',
    delete: 'Delete',
    emptyTitle: 'Your brain is empty',
    emptyBody: 'Keep ideas, links, notes and dates you do not want to forget here.',
    noResults: 'No results for that search.',
    newList: 'New list',
    listsEmptyTitle: 'No lists yet',
    listsEmptyBody: 'Create one for groceries, a trip or anything you need.',
    listProgress: '{done} of {total}',
    addItemTo: 'Add to {title}',
    removeItemNamed: 'Remove {text}',
    deleteListNamed: 'Delete the list {title}',
    itemsRemoved: '{count} item removed.|{count} items removed.',
    clearDone: 'Remove completed',
    linkedTitle: 'Linked notes',
  },
  account: {
    title: 'What an account adds',
    local: 'Without an account, HabitOS works fully on this device: habits, tasks, agenda, chat, Brain, journal and export. Your data stays here.',
    withTitle: 'With an account, you also get:',
    sync: 'Sync across your devices, with a copy on the server.',
    push: 'Reminder notifications and the morning summary.',
    whatsapp: 'Messaging the assistant on WhatsApp, if the server has it set up.',
    ai: 'AI help in the chat, if the server offers it.',
    access: 'No passwords: you sign in with a code sent to your email or with a passkey.',
    export: 'Exporting your data is always free, with or without an account.',
    cta: 'Create an account or sign in',
    settingsSignedOut: 'Optional: sync, get reminders and use WhatsApp',
    settingsSignedIn: 'Signed in',
    needs: {
      push: 'Reminders and the morning summary are sent from the server, so they need an account.',
      whatsapp: 'WhatsApp needs an account: the assistant replies from the server using your synced data.',
      premium: 'Upgrading to Premium needs an account: the plan is stored in it.',
      rest: 'Everything else keeps working on this device without an account.',
    },
  },
  sync: {
    title: 'Account and sync',
    intro: 'Create an account to sync habits across devices and keep a copy on the server. HabitOS keeps working offline.',
    email: 'Email',
    sendCode: 'Send code',
    codeSent: 'We sent a code to {email}. It expires in 10 minutes.',
    codeResent: 'We sent a new code to {email}. Use the one in the latest email: earlier codes no longer work.',
    codeHelp: 'You can paste the whole code; spaces and dashes are removed. You have 5 attempts.',
    resend: 'Resend code',
    resendIn: 'You can request another code in {count} second.|You can request another code in {count} seconds.',
    resendCapped: 'You already requested {count} codes in 15 minutes, the maximum. You can request another at {time}.',
    notArrived: 'If it does not arrive, check your spam folder and that the email is spelled correctly.',
    otherEmail: 'Use another email',
    passkeyWhat: 'With a passkey you sign in with your device fingerprint, face or PIN, without waiting for a code. You create it after signing in, on this same screen.',
    code: '6-digit code',
    verify: 'Verify',
    passkey: 'Sign in with a passkey',
    addPasskey: 'Add a passkey on this device',
    passkeyAdded: 'Passkey added.',
    signedInAs: 'Signed in as {email}',
    status: { idle: 'Synced', syncing: 'Syncing…', offline: 'Offline: changes will be sent when you reconnect', error: 'Sync error', pending: '{count} pending change|{count} pending changes' },
    lastSync: 'Last sync: {time}',
    never: 'Never',
    syncNow: 'Sync now',
    logout: 'Sign out',
    logoutWipe: 'Sign out and erase data on this device',
    logoutAll: 'Sign out on all devices',
    deleteAccount: 'Delete account',
    deleteAccountBody: 'Your account and all server data will be deleted. Export a copy first if you need it. To confirm, we will send you a code.',
    deleteAccountConfirm: 'Delete permanently',
    accountDeleted: 'Account deleted.',
    mergeTitle: 'This device already has data',
    mergeBody: 'The account may contain data from other devices. What should happen to the local data?',
    mergeKeep: 'Merge with the account',
    mergeDiscard: 'Discard local data',
    accountDataTitle: 'Your account data',
    accountDataHelp: 'Download what is stored on the server: your synced habits and entries plus account data (email, sessions, passkeys, plan and security log). It contains no passwords or keys. The file can be imported in Settings > Data.',
    accountDataDownload: 'Download my account data',
    sessionExpiredTitle: 'You were signed out',
    sessionExpiredBody: 'The session on this device expired or was closed from another device. Your data is still here; sign in again to keep syncing.',
    sessionExpiredAction: 'Sign in again',
    otherAccountTitle: 'This data belongs to another account',
    otherAccountBody: 'The data on this device belongs to {previous}. If you combine it, it will be copied into the account {current}.',
    otherAccountUnknown: 'another account',
    otherAccountMerge: 'Copy it into this account',
    unsyncedTitle: 'Some changes are not synced',
    unsyncedKeepBody: '{count} change has not reached your account yet. If you sign out now, it will stay only on this device.|{count} changes have not reached your account yet. If you sign out now, they will stay only on this device.',
    unsyncedWipeBody: '{count} change has not reached your account yet. If you sign out and erase the data now, it will be lost for good.|{count} changes have not reached your account yet. If you sign out and erase the data now, they will be lost for good.',
    unsyncedStay: 'Stay signed in',
    unsyncedLeave: 'Sign out anyway',
    rejected: '{count} change rejected by the server|{count} changes rejected by the server',
    rejectedHelp: 'They are still saved on this device but are not synced. Fix the cause (for example, archive a habit if you are over the plan limit) and tap Retry.',
    rejectedReason: {
      plan_limit: 'exceeds your plan limit',
      invalid: 'the server did not accept the data',
      other: 'the server did not accept it',
    },
    rejectedEntity: {
      settings: 'Settings',
      habits: 'Habit',
      entries: 'Entry',
      overrides: 'Skipped or failed day',
      groups: 'Group',
      vacations: 'Vacation',
      reminders: 'Reminder',
    },
    rejectedMore: 'and {count} more',
    rejectedRetry: 'Retry',
    rejectedRetried: '{count} change resent.|{count} changes resent.',
    unavailable: 'The sync server is unavailable. You can keep using HabitOS offline.',
    errors: {
      invalid_email: 'Enter a valid email.',
      invalid_code: 'The code is wrong or has expired.',
      too_many: 'Too many attempts. Wait a few minutes.',
      network: 'Could not reach the server.',
      generic: 'Something went wrong. Try again.',
      passkey_unsupported: 'This browser does not support passkeys.',
      passkey_failed: 'The passkey could not be used.',
    },
    devMailbox: 'Development mode: the code is printed in the server console.',
  },
  notifications: {
    snoozeNote: 'Reminder notifications include “Snooze 10 min” (on iPhone, web notifications do not show buttons).',
    brief: 'Morning summary',
    briefHelp: 'One notification a day with what you have today, calculated from your real data. Tapping it opens the chat.',
    briefTime: 'Summary time',
    briefNeedsPush: 'Enable notifications on this device to receive it.',
    briefFailed: 'Could not save. Try again when online.',
    title: 'Reminders',
    intro: 'Get a nudge if a habit is still pending. If you already did it, we will not remind you.',
    needInstallIos: 'On iPhone and iPad, add HabitOS to your Home Screen to receive notifications.',
    unsupported: 'This browser does not support push notifications.',
    denied: 'You blocked notifications. You can re-enable them in your browser settings for this site.',
    explainTitle: 'Allow notifications',
    explainBody: 'Your browser will ask for permission. We only send the reminders you set up.',
    enable: 'Enable notifications on this device',
    enabled: 'Notifications are on for this device.',
    disable: 'Turn off on this device',
    test: 'Send a test notification',
    testSent: 'Test notification sent.',
    notConfigured: 'The server has no notification keys configured.',
    add: 'Add reminder',
    time: 'Time',
    days: 'Days',
    daysScheduled: 'Habit’s scheduled days',
    none: 'No reminders.',
    planLimit: 'The free plan allows {limit} reminder per habit.',
    quietHours: 'Quiet hours',
    quietHelp: 'No reminders are sent during this window.',
    from: 'From',
    to: 'To',
  },
  plan: {
    title: 'Plan',
    free: 'Free',
    premium: 'Premium',
    current: 'Current plan: {plan}',
    validUntil: 'Active until {date}',
    settingsSub: 'Limits of each plan and what happens when you reach them',
    usage: 'You use {count} of {limit} active habits.',
    bothTitle: 'Included in both plans',
    both: 'Habits and tasks, hourly agenda, chat with the assistant, Brain and lists, journal and weekly review, focus, offline use, export and import.',
    bothAccount: 'With an account, on any plan: sync across devices, reminder notifications, the morning summary and WhatsApp (if the server has it set up).',
    limitsTitle: 'Limits',
    rows: { habits: 'Active habits', reminders: 'Reminders per habit', trends: 'Weekly trends in Progress', export: 'Export your data' },
    yes: 'Yes',
    no: 'No',
    exportFree: 'Exporting is always free: on any plan, with or without an account (Settings → Data).',
    atLimitTitle: 'What happens at the limit',
    atLimit: {
      create: 'You cannot create more active habits or tasks. The app and the chat warn you before saving, and nothing is saved halfway.',
      archive: 'Archiving a habit or completing a task frees its slot. Archived items keep their history and can be restored.',
      sync: 'If another device goes over the limit, the server rejects those changes and explains them in Account and sync. They stay on that device until you resolve it.',
      expired: 'If Premium ends, you keep all your habits and can edit them; only new ones follow the limit of {limit} active habits.',
    },
    premiumAdds: 'Premium raises the limits and adds weekly trends. Nothing else changes.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    upgrade: 'Upgrade to Premium',
    cancel: 'Cancel subscription',
    cancelled: 'The subscription will not renew.',
    testMode: 'Test environment: no real charges are made. Activation is simulated and only for testing the app.',
    unavailable: 'Payments are not configured on this server.',
    activated: 'Premium activated (test mode).',
  },
  timer: {
    title: 'Timer',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop and log',
    discard: 'Discard',
    running: 'Running',
  },
  errors: {
    title: 'Something went wrong',
    body: 'The app hit an unexpected error. Your data is safe on this device.',
    reload: 'Reload',
    storage: 'Local storage is unavailable. If you are in private mode, leave it or allow storage for this site.',
    future_date: 'You cannot log future days.',
    before_start: 'That day is before the habit started.',
    not_scheduled: 'That day is not scheduled.',
    same_day: 'Pick a different day.',
    not_movable: 'This habit cannot be moved: only specific days can move, not quotas or to-dos.',
    source_not_scheduled: 'That day is no longer scheduled, so there is nothing to move.',
    target_out_of_range: 'That day falls outside the habit period.',
    target_scheduled: 'This habit already has that day taken.',
    save: 'Could not save. Try again.',
  },
  assistantSettings: {
    title: 'Assistant and Brain',
    summary: 'AI, memory, privacy, notes, lists, journal and integrations',
    ai: {
      checking: 'Checking…',
      connected: 'Connected',
      use: 'Use AI when the rules do not understand',
      useHelpLocal: 'Only consulted when the rules are unsure. The model runs on your computer (Ollama).',
      useHelpRemote: 'Only consulted when the rules are unsure. The message is sent to the provider configured on the server.',
      providers: { none: 'No AI', ollama: 'Ollama (local)', openai: 'OpenAI-compatible provider' },
      detail: {
        ok: 'The model responds. Phrases the rules do not understand are sent to it.',
        disabled: 'The server has no AI configured.',
        unreachable: 'The configured AI does not respond (is Ollama running?). Using the rules.',
        model_missing: 'Ollama responds, but the configured model is not downloaded.',
        error: 'The configured AI returned an error. Using the rules.',
        offline: 'No connection to the HabitOS server: using this device’s rules.',
      },
      howTo: 'How to enable a free AI',
      step1: 'Install Ollama (free and open source) and download a model, for example: ollama pull qwen3:4b.',
      step2: 'In the server .env file: AI_PROVIDER=ollama and AI_MODEL=qwen3:4b.',
      step3: 'Restart the HabitOS server. You will see the real connection status here.',
      title: 'Artificial intelligence',
      rules: 'Rule-based understanding on this device',
      notConnected: 'No AI connected',
      rulesHelp: 'The assistant understands phrases with local rules: it works offline and sends nothing out. When an AI model is connected, it will appear here with its real status.',
    },
    prefs: {
      title: 'Preferences',
      brief: 'Daily summary when opening the chat',
      briefHelp: 'Once a day: today’s progress, what is coming up and overdue tasks. Calculated on this device.',
    },
    prefsMemory: {
      title: 'What I remember about you',
      body: 'Preferences you asked me to remember. I use them as a starting point when planning and always explain it. Only on this device.',
      forget: 'Forget',
      forgetAll: 'Forget everything',
      forgotten: 'Preference forgotten',
    },
    memory: {
      title: 'Memory and context',
      body: 'The chat remembers the current conversation to understand references like “that habit” or “the second one”. It is stored only on this device and deleted after {hours} h of inactivity, on sign-out or when linking another account.',
      current: 'Current conversation: {count} messages (last activity: {date}).',
      empty: 'There is no saved conversation.',
      clear: 'Delete the conversation',
      cleared: 'Conversation deleted',
    },
    privacy: {
      aiLocal: 'Phrases the rules do not understand are sent to the AI on your own computer (Ollama), with the names of your habits and tasks. Nothing goes to the internet.',
      aiRemote: 'Phrases the rules do not understand are sent to the AI provider configured on the server, with the names of your habits and tasks. Never your logs, notes or journal.',
      title: 'Privacy',
      local: 'Your habits, tasks, notes and journal do not leave this device for the assistant to understand them.',
      confirm: 'The assistant never saves, changes or deletes anything until you confirm the proposal.',
      journal: 'Journal and note text is not copied into the conversation: it is only shown when you ask.',
    },
    brain: {
      title: 'Brain',
      body: 'Your notes, lists and journal. The chat stores here what you ask it to remember.',
    },
    whatsapp: {
      what: 'Message the assistant from WhatsApp: questions and proposals with buttons to confirm and undo. Changes reach the app when it syncs.',
      notConfigured: 'The HabitOS server has no WhatsApp configured. It requires a Meta app (WhatsApp Cloud API) and a public URL; Meta may ask for a payment method. See docs/whatsapp.md.',
      getCode: 'Link my WhatsApp',
      sendCode: 'Send this message from your WhatsApp to {number}:',
      theNumber: 'the assistant number',
      expires: 'The code expires at {time} and works only once.',
      linked: 'Linked to number {number}.',
      unlink: 'Unlink',
      privacy: 'Messages go through WhatsApp (Meta) servers. Nothing is saved until you confirm.',
    },
    integrations: {
      title: 'Integrations',
      whatsapp: { name: 'WhatsApp', body: 'Message the assistant from WhatsApp. Requires server setup.' },
      googleCalendar: { name: 'Google Calendar', body: 'See your events next to your tasks. Not available yet.' },
    },
    status: { available: 'Active', setup: 'Needs setup', unavailable: 'Not available' },
  },
  chat: {
    title: 'Chat',
    shortcuts: { label: 'Shortcuts', agenda: 'Agenda', lists: 'Lists', notes: 'Notes', journal: 'Journal' },
    intro: 'Chat with the assistant. It can look up your data and propose changes; nothing is saved until you confirm.',
    privacy: 'Runs on this device and sends no data out. The conversation is deleted after 24 h of inactivity.',
    privacyAi: 'AI is only consulted when the rules do not understand; it receives the message and your habit names, never your logs. The conversation is deleted after 24 h of inactivity.',
    ai: { on: 'AI: {provider} · {model}', off: 'No AI: rules on this device' },
    newConversation: 'New conversation',
    history: 'Conversation with the assistant',
    loading: 'Loading conversation…',
    emptyTitle: 'How can I help?',
    emptyBody: 'I can plan goals and your week, answer questions about your habits, tasks and agenda, and log things. I always show you the proposal before saving.',
    placeholder: 'Type a message…',
    placeholderPending: 'Reply or ask for a change…',
    send: 'Send',
    you: 'You',
    thinking: 'Thinking…',
    applying: 'Saving…',
    options: 'Options',
    suggestions: 'Suggestions',
    errors: {
      load: 'The conversation could not be loaded. Close and reopen the assistant.',
      step: 'Something went wrong while processing the message. No changes were saved.',
    },
    examples: {
      routine: 'Organízame la semana para estudiar, entrenar y descansar',
      goal: 'Quiero estudiar programación 30 minutos al día, excepto los miércoles',
      week: 'Quiero organizar mi semana',
      neglected: '¿Qué hábitos estoy descuidando últimamente?',
      agenda: '¿Qué tengo mañana?',
      task: 'Tarea preparar la presentación',
    },
    say: {
      undoNo: 'No, déjalo',
      alternative: 'Muéstrame otra alternativa',
      anyTime: 'Cuando mejor encaje',
      remind: 'Ponle un recordatorio a {name}',
      progress: '¿Cómo voy con {name}?',
      neglected: '¿Qué hábitos estoy descuidando últimamente?',
      planWeek: 'Quiero organizar mi semana',
      startFocus: 'Pomodoro',
      agendaWeek: '¿Qué tengo esta semana?',
      availWeekdayEvenings: 'Entre semana de 18 a 20',
      availMornings: 'Todos los días de 8 a 10 de la mañana',
      availWeekend: 'El fin de semana de 10 a 13',
    },
    intent: {
      remember_pref: 'Remember a preference',
      query_prefs: 'See what I remember about you',
      plan_routine: 'Plan a routine with several activities',
      free_day: 'Keep a day free',
      move_day: 'Move a habit or task to another day',
      alternative: 'See another alternative',
      undo_last: 'Undo the last change',
      query_habits: 'See my habits',
      query_stats: 'See habit statistics',
      query_tasks: 'See pending tasks',
      query_agenda: 'Check the agenda',
      query_focus: 'See focus time',
      query_notes: 'Search my notes',
      query_journal: 'Read the journal',
      plan_week: 'Organize the week',
      plan_goal: 'Turn it into a planned habit',
      edit_habit: 'Change a habit or its reminder',
      reschedule_task: 'Reschedule a task',
      log_focus: 'Log a focus session',
    },
    help: {
      plan: 'Plan a goal: «Quiero leer 20 minutos al día salvo el domingo».',
      week: 'Organize your week around your availability.',
      stats: 'Answer with your data: neglected habits, progress of a habit, summary.',
      agenda: 'Check the agenda, pending tasks, focus time, notes and, if you ask, the journal.',
      edit: 'Change a habit’s goal, days or reminder time, or reschedule tasks.',
      log: 'Log activity or focus sessions.',
      capture: 'Create tasks, reminders, notes, ideas, list items and journal entries.',
      safety: 'I show you the proposal before saving. You can change it, cancel it or undo it.',
    },
    c: {
      savePref: 'Yes, remember it',
      keepNew: 'Keep only {count} new one|Keep only {count} new ones',
      archiveSome: 'Archive a habit',
      seePlans: 'See plans',
      createNew: 'Create “{name}” as a new habit',
      fixDays: 'Fix specific days',
      anyTime: 'Whenever fits best',
      removeDay: 'Remove {day}',
      undoNo: 'No, leave it',
      undoYes: 'Yes, undo',
      yes: 'Yes, that',
      no: 'No, I will rephrase',
      none: 'None of these',
      option: '{summary}',
      target: '{name}',
      daily: 'Every day',
      weekdays: 'Monday to Friday',
      quota: '{count} time per week|{count} times per week',
      morningAt: 'Morning ({time})',
      afternoonAt: 'Afternoon ({time})',
      eveningAt: 'Evening ({time})',
      at: 'At {time}',
      noTime: 'No fixed time',
      tomorrow: 'Tomorrow',
      nextMonday: 'Next Monday',
      remind: 'Reminder for «{name}»',
      progress: 'How «{name}» is going',
      neglected: 'Neglected habits',
      createHabit: 'Plan a habit',
      createTask: 'Create a task',
      planWeek: 'Organize the week',
      agendaWeek: 'See this week’s agenda',
      openAgenda: 'Open the agenda',
      openBrain: 'Open the brain',
      startFocus: 'Start a pomodoro',
      availWeekdayEvenings: 'Weekdays, 18–20',
      availMornings: 'Mornings, 8–10',
      availWeekend: 'Weekend, 10–13',
      otherAvailability: 'Give other availability',
    },
    q: {
      didYouMean: 'Do you want this: {what}?',
      whichIntent: 'I am not sure what you are asking. Do you mean one of these?',
      activity: 'Which activity do you want to plan?',
      days: 'Which days do you want to do «{name}»?',
      daysAgain: 'I did not recognize the days. Try «todos los días», «de lunes a viernes» or «excepto los miércoles».',
      goalTime: 'Do you want a fixed time? Then I can check for clashes and set a reminder. You can also leave it without a time.',
      newTime: 'At what time?',
      timeAgain: 'I did not recognize the time. Type it, for example «a las 19:00» or «por la mañana».',
      ambiguousTime: 'At {a} or at {b}?',
      availability: 'When are you free this week? For example: «entre semana de 18 a 20 y el sábado por la mañana».',
      availabilityHint: 'You have {count} untimed task to place.|You have {count} untimed tasks to place.',
      availabilityAgain: 'I could not get a schedule from that. Give days and hours, for example «de lunes a jueves de 19 a 21».',
      rangeAmPm: 'Are those hours in the morning or the afternoon? For example «de 6 a 8 de la tarde».',
      whichHabit: 'Which habit?',
      whichTask: 'Which task?',
      noHabits: 'You have no active habits.',
      noTasks: 'You have no pending tasks.',
      notFoundTarget: 'I cannot find «{name}». Pick one from the list or type its name.',
      whatChange: 'What do you want to change in «{name}»? You can give the reminder time, the duration or the days.',
      reminderTime: 'At what time do you want the reminder for «{name}»?',
      whenTask: 'When should «{name}» move to?',
      focusMinutes: 'How many minutes did the session last?',
      destructive: 'This hides or deletes data. To continue, press «{verb}» on the proposal or type «{verb}».',
    },
    r: {
      viaAi: 'Interpreted with help from the AI. Review the proposal before confirming.',
      greeting: 'Hi. Tell me what you want to do or ask me about your habits, tasks or agenda.',
      thanks: 'You are welcome.',
      helpIntro: 'This is what I can do, always with the data on this device:',
      unknown: 'I did not understand the message and I would rather not guess. Try one of these suggestions or rephrase it.',
      rephrase: 'OK. Write it in other words.',
      cancelled: 'Cancelled. Nothing was changed.',
      questionCancelled: 'OK, we leave it. Nothing was changed.',
      nothingToCancel: 'There is nothing pending to cancel.',
      nothingToConfirm: 'There is no proposal waiting for confirmation.',
      nothingToModify: 'There is no open proposal to change. Tell me what you want to do.',
      notPending: 'That proposal is no longer pending.',
      staleChoice: 'That option is no longer available. Type what you need.',
      staleProposal: 'Part of the proposal no longer exists (maybe it was deleted on another device). Nothing was saved.',
      noChange: 'I did not see what to change. Say, for example, «a las 19:00», «45 minutos» or «los martes y jueves».',
      cantModify: 'This proposal cannot be adjusted that way. Cancel it and write it again.',
      removeNotFound: 'I cannot find that task in the proposal.',
      weekModifyHelp: 'You can say «mueve <tarea> al jueves a las 19», «quita <tarea>» or give other availability («de 19 a 21 entre semana»).',
      weekEmptyAfterChange: 'Without those tasks the proposal is empty. I cancelled it; nothing was changed.',
      opening: 'Opening…',
      reset: 'New conversation. The previous one was deleted from this device.',
      applyFailed: 'It could not be saved.',
      nothingSaved: 'No changes were saved.',
      nothingSavedRetry: 'No changes were saved. You can confirm again.',
      undoHint: 'You can undo it from the proposal while the app stays open.',
      undoFailed: 'It could not be undone. Check it in the corresponding screen.',
      interrupted: 'The app closed while a proposal was being saved. I do not know whether it was saved: check before repeating it.',
    },
    p: {
      introAdjust: 'You already have “{name}”. I suggest adjusting it instead of creating another one:',
      orCreateNew: 'Would you rather create a new habit?',
      quotaHint: 'With “any days you like” it does not appear on specific agenda days. I can fix the days if you want.',
      altFixedDays: 'Fixed days: {days}.',
      altTime: 'Another time without clashes: {time}.',
      changedWeekly: 'Weekly total: {minutes} min.',
      changedFree: 'Free: {days}.',
      intro: 'Here is my proposal. Review it before confirming:',
      introGoal: 'This is how I understood it. Review the proposal:',
      introWeek: 'Proposal for your week ({count} task):|Proposal for your week ({count} tasks):',
      updated: 'I updated the proposal (revision {revision}):',
      planTitle: 'Plan with {count} change|Plan with {count} changes',
      revision: 'revision {revision}',
      why: 'How I decided',
      change: 'Change',
      destructiveNote: 'This hides or deletes data. You can undo it right after.',
      previousDiscarded: 'The previous proposal was discarded without saving.',
      seeBelow: '(Updated proposal below.)',
      gone: '(Old proposal, no longer available.)',
      changedTime: 'Time: {time}',
      changedNoTime: 'No fixed time',
      changedMinutes: 'Duration: {minutes} min',
      changedDays: 'Days changed',
      changedDate: 'Date changed',
      changedName: 'Name: {name}',
      changedAvailability: 'New availability',
      moved: '«{name}» moved',
      removed: '«{name}» removed from the plan',
    },
    plan: {
      weekly: '{total} per week split into {sessions} sessions of {minutes} minutes.',
      freeDays: 'Keeping free: {days}.',
      adjustExisting: '“{name}” already exists and is the same activity as “{activity}”: I adjust it to avoid a duplicate.',
      activity: 'Activity: «{name}».',
      daily: 'Every day.',
      days: 'Days: {days}.',
      daysExcept: 'Days: {days} (without {except}, as you asked).',
      quota: '{count} time per week, on the day you prefer.|{count} times per week, on the days you prefer.',
      minutes: 'Goal of {minutes} minutes per day: you can log partial sessions.',
      noMinutes: 'No duration: it is marked as done. If you want a duration, tell me («30 minutos»).',
      time: 'Time: from {time} to {end}, with a reminder at {time}.',
      timeFromSlot: 'Suggested time: {time} (you gave a part of the day, not a time). From {time} to {end}.',
      noTime: 'No fixed time: it will appear under «Anytime» in the agenda.',
      noConflicts: 'I saw no clashes with timed tasks or reminders in the next {days} days.',
      conflicts: 'There is {count} overlap with what you already have in the next {days} days:|There are {count} overlaps with what you already have in the next {days} days:',
      conflict: '{date} at {time}: «{name}»',
      agendaScope: 'I only check what HabitOS knows (timed tasks and reminders). I have no access to other calendars.',
      reminderNote: 'The alert arrives as a notification only if you have an account and notifications enabled on this device.',
      duplicate: 'You already have a habit called «{name}». To change it instead of creating another, cancel and say «cambia {name}».',
      editFromToday: 'The change applies from today; earlier history is kept as it was.',
    },
    week: {
      period: 'Period: {from} to {to}.',
      window: 'Availability: {days}, from {start} to {end}.',
      assumedAllDays: 'You gave no days, so I used that time range every day.',
      order: 'Overdue first, then tasks with a date, and undated ones last.',
      block: 'I reserved {minutes} minutes per task because HabitOS does not store how long each one takes.',
      avoided: 'I avoided times when you already have tasks or reminders.',
      habits: 'Your {count} habit stays the same; I only propose times for tasks.|Your {count} habits stay the same; I only propose times for tasks.',
      unplaced: 'These do not fit your availability: {names}. They stay as they are.',
      daysWithoutRange: 'You gave no time range for: {days}. I did not use them.',
      noTasks: 'You have no untimed pending tasks for the next 7 days, so there is nothing to organize.',
      noTasksDetail: 'I consider overdue tasks, undated ones and those dated this week without a time. Habits already repeat on their own.',
      nothingFits: 'With that availability I could not place any task. I proposed no changes.',
    },
    limit: {
      routine: 'This routine adds {adds} habits; on the free plan you would have {count} slot left.|This routine adds {adds} habits; on the free plan you would have {count} slots left.',
      routineOne: 'This routine adds 1 habit; on the free plan you would have {count} slot left.|This routine adds 1 habit; on the free plan you would have {count} slots left.',
      proposal: 'This proposal adds {adds} habits; on the free plan you would have {count} slot left.|This proposal adds {adds} habits; on the free plan you would have {count} slots left.',
      proposalOne: 'This proposal adds 1 habit; on the free plan you would have {count} slot left.|This proposal adds 1 habit; on the free plan you would have {count} slots left.',
      last: 'This adds {count} habit and uses the last slot of the free plan ({limit} active habits).|This adds {count} habits and uses the last slots of the free plan ({limit} active habits).',
      over: 'This adds {adds} habits, but your plan has only {count} slot left ({active} of {limit} active habits in use).|This adds {adds} habits, but your plan has only {count} slots left ({active} of {limit} active habits in use).',
      full: 'You already have {count} of the {limit} active habits your plan allows: no new one fits.',
      adjust: 'This does not fit your plan. Do you want to adjust it before saving?',
      blocked: 'I will not save it like this: it would exceed your plan limit. You can adjust it first.',
      nothingSaved: 'Nothing has been saved. Archiving a habit frees its slot and keeps its history.',
      alreadyFits: 'The proposal already fits your plan.',
      nothingLeft: 'Without new habits there is nothing left to propose. No changes were saved.',
      removed: 'Removed: {names}.',
    },
    routine: {
      intro: 'Here is your routine with {count} activity. Review it:|Here is your routine with {count} activities. Review it:',
      askAvailability: 'To organize {names}, when are you available?',
      askHint: 'For example: “weekdays from 6 to 9 pm and Saturday morning”. If you do not mind, I pick times without clashes.',
      line: '{name}: {days}, {minutes} min at {time}.',
      lineDefault: '{name}: {days}, {minutes} min at {time} (starting values; tell me if you prefer others).',
      adjusts: 'For “{activity}” I adjust your habit “{name}” instead of creating another one.',
      noOverlap: 'Times do not clash with each other or with your tasks and reminders for the next {days} days.',
      howToChange: 'You can say “study at 7 pm”, “train 45 minutes”, “keep Saturday free” or “show me another alternative”.',
      conflict: 'I found no free time for {name}: it would be at {time} and clashes with something.',
      noDays: 'No day is left for {name} with the requested free days.',
      tasksLeft: 'You have {count} task without a time; to place it, say “organize my week”.|You have {count} tasks without a time; to place them, say “organize my week”.',
      nothing: 'I could not build the routine with that information. No changes proposed.',
      whichActivity: 'Which one do you mean? {names}.',
      changed: 'Updated: {name}.',
      modifyHelp: 'Tell me what to change and for which activity, for example “study at 7 pm” or “train on Tuesdays and Thursdays”.',
    },
    free: {
      intro: 'To keep {days} free, I propose:',
      period: 'It affects tasks in the next 7 days and, from today, habits with fixed days.',
      onlyThatDay: '“{name}” only happens on that day: I leave it unchanged.',
      flexible: '“X times per week” habits have no fixed days and do not change: {names}.',
      alreadyFree: 'You have nothing planned for {days} in the next 7 days and no habits fixed on that day.',
    },
    move: {
      explain: '“{name}” moves from {from} to {to}. Days from today: {days}.',
      flexible: '“{name}” is an “X times per week” habit: it has no fixed days to move. Do it any day you like.',
      unsupported: '“{name}” has a monthly or interval schedule; change it from the habit form.',
      notThatDay: '“{name}” does not happen on {day}. Current days: {days}.',
      alreadyBoth: '“{name}” already happens on {from} and {to}. Do you want to remove {from}?',
    },
    alt: {
      nothing: 'There is no proposal in progress to find another version of.',
      none: 'I cannot find a better alternative without clashes. The proposal stays the same.',
      notForThis: 'There is no other version for this proposal; tell me what to change.',
      routine: 'Another distribution of days and times.',
      weekLate: 'Tasks as late as possible within the week.',
      weekEarly: 'Tasks as early as possible.',
    },
    undo: {
      nothing: 'There is no change saved from the chat to undo.',
      confirm: 'Undo the last saved change: {what}?',
      partial: 'Undone, except {count} item you changed afterwards: I left it as it is.|Undone, except {count} items you changed afterwards: I left them as they are.',
    },
    brief: {
      pushTitle: 'Your day in HabitOS',
      pushEmpty: 'Nothing planned today. Tap to plan with the chat.',
      pushProgress: 'You have {count} thing today.|You have {count} things today.',
      pushNext: 'Next: {time} · {name}.',
      morning: 'Good morning. Here is your day:',
      afternoon: 'Good afternoon. Here is how your day is going:',
      evening: 'Good evening. Here is how your day went:',
      empty: 'You have nothing planned today. Shall we create a habit or plan the week?',
      progress: '{done} of {total} done.|{done} of {total} done.',
      next: 'Coming up',
      nextItem: '{time} · {name}',
      overdue: 'You have {count} overdue task.|You have {count} overdue tasks.',
      conflicts: 'There is {count} schedule clash today.|There are {count} schedule clashes today.',
      source: 'Calculated from your habits, tasks and reminders for today on this device.',
    },
    cards: { agenda: 'Agenda', habits: 'Habits', tasks: 'Tasks', gone: 'It no longer exists: it was deleted or archived.' },
    wa: {
      linked: 'Done: this number is now linked to your HabitOS account. Write what you need, for example “what do I have today?”.',
      notLinked: 'Hi. To use the HabitOS assistant on WhatsApp, open the app → Settings → Assistant and Brain → WhatsApp, generate a code and send me “vincular” followed by the code.',
      badCode: 'That code is not valid or has expired. Generate a new one in the app (Settings → Assistant and Brain → WhatsApp).',
      otherAccount: 'This number is already linked to another account. Unlink it from that account first.',
      tooMany: 'Too many attempts with wrong codes. Wait a few minutes and generate a new code.',
      unsupported: 'For now I only understand text messages and the buttons in my replies.',
      noData: 'I do not have your data on the server yet. Open HabitOS signed in to sync, then write again.',
      appOnly: 'That can only be done from the app (for example, starting a timer).',
      openApp: 'That is better in the app: {url}',
      confirm: 'Confirm',
      replyNumber: 'Reply with the option number.',
    },
    prefs: {
      confirm: 'Should I remember this for your next plans? {what}',
      saved: 'I will remember: {what}',
      failed: 'I could not save the preference. Nothing changed.',
      none: 'You have not asked me to remember any preference yet.',
      howTo: 'For example: “prefiero entrenar por la mañana”, “no me pongas nada antes de las 8” or “los domingos quiero descansar”.',
      list: 'This is what I remember about you:',
      item: '{text}',
      where: 'Stored only on this device. You can see and delete it in Settings → Assistant and Brain.',
      usedSlot: 'Since you prefer doing it {slot}, I suggest that slot.',
      usedSlotFor: '{name}: {slot}, as you prefer.',
      noTimeInLimits: 'I found no time for {name} within your time limits: I placed it anyway; change it if you like.',
      appOnly: 'Preferences are stored in the app, on your device. Tell me from the HabitOS chat.',
      slots: { morning: 'in the morning', afternoon: 'in the afternoon', evening: 'in the evening' },
      describe: {
        earliest: 'nothing before {time}',
        latest: 'nothing after {time}',
        slot: '{activity}, {slot}',
        freeDays: 'free: {days}',
      },
    },
    voice: {
      start: 'Dictate',
      stop: 'Stop dictating',
      listening: 'Listening… Review the text before sending.',
      privacy: 'Dictation uses the browser speech recognition (in Chrome, audio is processed by Google).',
      error: 'Could not use the microphone. Check the browser permission.',
    },
    fields: {
      reminderAt: 'At {time}',
      noSchedule: 'No fixed time',
      from: 'From',
      fromToday: 'Today (history does not change)',
      days: 'Days',
      scheduledDays: 'Scheduled days',
      task: 'Task',
    },
    a: {
      noHabits: 'You have no active habits yet, so there are no statistics to compute.',
      notEnoughData: 'There are not enough records yet to draw conclusions.',
      neglectedIntro: 'From your records, {count} habit is below {threshold} % completion:|From your records, {count} habits are below {threshold} % completion:',
      neglectedNone: 'No habit is below {threshold} % completion. Good work.',
      lowest: 'The ones with the lowest completion:',
      habitRate: '«{name}»: {done} of {total} completed ({pct} %), {last}',
      lastOn: 'last logged on {date}',
      neverLogged: 'never logged',
      fewData: 'Still little data (fewer than {min} periods): {names}.',
      fewDataOne: 'There is still little data (fewer than {min} periods) to draw conclusions.',
      periodStats: 'Period: {from} to {to} ({days} days). Today does not count because it is still open.',
      sourceStats: 'Data: your habit records on this device. Skipped days and vacations do not count as misses.',
      sourceLocal: 'Data from this device.',
      summary: 'In the last 7 days you completed {done} of {total} periods ({pct} %).',
      progressTitle: 'How «{name}» is going:',
      streak: 'Current streak: {current} {unit} (best: {best})',
      rate30: 'Last 30 days: {done} of {total} completed ({pct} %)',
      rate30None: 'Last 30 days: no periods to evaluate',
      completions: 'Total times completed: {count}',
      periodProgress: 'Computed from all your records since {from}.',
      habitsIntro: 'You have {count} active habit:|You have {count} active habits:',
      habitLine: '«{name}» · {freq} · today: {today}',
      noTasks: 'You have no pending tasks.',
      tasksIntro: 'You have {count} pending task:|You have {count} pending tasks:',
      taskItem: '«{name}» · {when}',
      undated: 'no date',
      overdue: 'overdue ({date})',
      today: 'today',
      more: 'And {count} more.',
      tasksHint: 'Overdue: {overdue}. Undated: {undated}. I can help you spread them over the week.',
      agendaDay: '{date} · {pending} pending, {done} done',
      agendaItem: '{time} · {name} ({kind}){state}',
      doneMark: ' · done',
      overdueMark: ' · overdue',
      agendaEmpty: 'You have nothing planned: {period}.',
      agendaWeek: 'This is what you have planned ({period}): {count} items.',
      range: '{from} to {to}',
      agendaSource: 'Includes scheduled habits, tasks and important dates from HabitOS. External calendars are not included.',
      focus: 'Today you have {today} min of focus. This week, {week} min in {count} session.|Today you have {today} min of focus. This week, {week} min in {count} sessions.',
      focusNone: 'No focus sessions logged this week.',
      focusItem: '{name}: {minutes} min',
      periodWeek: 'Week from {from} to {to}.',
      notesFound: 'I found {count} note with «{query}»:|I found {count} notes with «{query}»:',
      notesRecent: 'Your {count} most recent notes:',
      notesNone: 'I found no notes with «{query}».',
      notesEmpty: 'You have no notes yet.',
      notesSource: 'I search titles, text, tags and list items.',
      notesGone: '(Those notes no longer exist.)',
      journalOn: 'Your journal for {date}:',
      journalNone: 'There are no journal entries.',
      journalNoneOn: 'There is no journal entry for {date}.',
      journalPrivacy: 'I only read the journal when you ask, and its text is not copied into the conversation.',
      journalGone: '(That entry no longer exists.)',
    },
    kind: { habit: 'habit', task: 'task', date: 'date' },
    unit: { day: 'day|days', week: 'week|weeks', month: 'month|months' },
    weekday: { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' },
    freq: { other: 'custom frequency' },
    status: {
      not_scheduled: 'not due',
      pending: 'pending',
      completed: 'done',
      missed: 'missed',
      failed: 'over the limit',
      skipped: 'skipped',
      vacation: 'vacation',
      proposal: {
        pending: 'Awaiting confirmation',
        applying: 'Saving',
        applied: 'Saved',
        cancelled: 'Discarded',
        failed: 'Not saved',
        interrupted: 'Interrupted',
        undone: 'Undone',
      },
    },
  },
  update: { available: 'A new version is available.', reload: 'Update', offlineReady: 'HabitOS is ready to work offline.' },
};
